/*
 * Copyright (c) 2020. American Express Company
 */

import _ from 'lodash';
import BWDataService from './bw-data.service';
import { CashFlowModel } from 'src/types/cash-flow.model';
import { BudgetUserModel, Summary, PlanType, UserUtils, FullUserModel, UserModel } from 'src/types/user.model';
import StorageService from './storage.service';
import { LocalDate } from '@js-joda/core';

export interface IncomeInterface {
    name: string;
    daily: number;
    weekly: number;
    monthly: number;
    quarterly: number;
    annually: number;
}

export default class UserService extends BWDataService {
    public static async getUser(): Promise<UserModel> {
        const url: string = '/users/';

        const payload: any = await this.get(url);

        // if payload has cashFlow add endDate to each cashflow entry
        if (payload.cashFlow) {
            payload.cashFlow = payload.cashFlow.map((newcf: any) => ({
                ...newcf,
                endDate: LocalDate.parse(newcf.startDate).plusMonths(1).toJSON() // always one month later
            }));
        }

        if (payload.defaultPlan.isTypeBudgeting) {
            const budgetUser: BudgetUserModel = { planType: PlanType.BUDGETING, ...payload };
            return UserUtils.deserializeDates(budgetUser);
        }

        let user: FullUserModel = { planType: PlanType.FULL, ...payload };
        user = UserUtils.deserializeDates(user);

        if (user) {
            this.zoomCashFlow(user);
        }

        return user;
    }

    public static async updateUser(user: FullUserModel): Promise<any> {
        return await this.put('/users/', {
            forename: user.forename,
            surname: user.surname,
            dob: user.dob
        });
    }

    public static async deleteUser(): Promise<any> {
        await this.delete('/users/');
        BWDataService.clearUser();
        window.sessionStorage.clear();
        window.localStorage.clear();
    }

    public static async unenrollUser(user: UserModel): Promise<any> {
        await this.post(`/unenroll/budget/${user.defaultPlan.planUid}`, null);
    }

    public static async getIncome(): Promise<IncomeInterface> {
        const url: string = '/users/income';
        return await this.get(url);
    }

    // convert monthly cashFlow into monthly, yearly, etc
    public static zoomCashFlow(user: FullUserModel): number {
        if (!user) {
            return 0;
        }

        let zoom: string = StorageService.getZoom();
        if (!zoom) {
            zoom = 'years';
            StorageService.setZoom(zoom);
        }

        if (!user.rawCashFlow) {
            user.rawCashFlow = _.cloneDeep(user.cashFlow);
            user.rawSummaries = _.cloneDeep(user.summaries);
        }

        let monthsPerPeriod = 0;
        switch (zoom) {
            case 'months':
                monthsPerPeriod = 1;
                break;
            case 'years':
            case 'overall':
                monthsPerPeriod = 12;
                break;
        }

        StorageService.setMonthsPerPeriod(monthsPerPeriod);

        if (zoom === 'months') {
            user.cashFlow = user.rawCashFlow;
            user.summaries = user.rawSummaries;
            return monthsPerPeriod;
        }

        // only show complete years, if the last month isn't December, cut off that year
        let finalMonth: LocalDate = _.last(user.rawCashFlow).startDate;
        const finalMonthNum: number = 1;
        try {
            finalMonth.monthValue();
        } catch (e) {
            console.error('could not parse final month value', e);
        }

        if (finalMonthNum < 12) {
            finalMonth = finalMonth.minusYears(1).withMonth(12);
        }

        user.cashFlow = _(user.rawCashFlow)
            // only include months before and including the finalMonth
            .filter((cf: CashFlowModel) => cf.startDate.compareTo(finalMonth) <= 0)
            // group by the period (sets of x months in each period)
            .groupBy((cf: CashFlowModel) =>
                Math.floor((cf.startDate.year() * 12 + (cf.startDate.monthValue() - 1)) / monthsPerPeriod)
            )
            // combine the values depending on the individual fields
            .mapValues((cf) => ({
                // startDate is the first startDate
                startDate: _.head(cf).startDate,

                // endDate is the last endDate
                endDate: _.last(cf).endDate,

                // other values are sums or lasts depending on being deltas or balances
                income: _.sumBy(cf, 'income'),
                expense: _.sumBy(cf, 'expense'),
                investment: _.last(cf).investment,
                debt: _.last(cf).debt,
                freeCash: _.last(cf).freeCash,
                dangerZone: !!_.find(cf, { dangerZone: true }),
                danger: _.maxBy(cf, 'danger').danger
            }))
            .values()
            .value();

        // this is only for years and overall, if it was months, it returned above
        user.summaries = _(user.rawSummaries)
            .groupBy((s: Summary) => s.year.toString() + s.id.toString())
            .mapValues((summaries: Summary[]) => {
                return {
                    sum: _.sumBy(summaries, function (summary: Summary) {
                        return summary.sum;
                    }),
                    name: summaries[0].name,
                    month: 1,
                    year: summaries[0].year,
                    id: summaries[0].id
                } as Summary;
            })
            .values()
            .value() as any[] as Summary[];

        return monthsPerPeriod;
    }

    public static async postAttributes(attributes: any): Promise<any> {
        return await this.post('/users/attributes', attributes);
    }

    public static async getAttributes(): Promise<any> {
        return await this.get('/users/attributes');
    }

    public static async putAttributes(attributes: any): Promise<any> {
        return await this.put('/users/attributes', attributes);
    }

    public static async deleteAttribute(attribute: string): Promise<any> {
        // use raw so we don't report 404 errors if the attribute didn't exist
        return await this.deleteRaw('/users/attributes/' + attribute);
    }
}
